import React from 'react';
import { Box, Button, useTheme } from '@mui/material';
import type { RoleplayStatus } from '../types';

import feedbackIcon from '../../../assets/images/ic_report_1.svg';
import cancelIcon from '../../../assets/images/cancel_icon.svg';
import micIcon from '../../../assets/images/ic_mic_1.svg';

export interface RoleplayMobileControlBarProps {
  sessionTime: number;
  estimatedTime: number;
  formatTime: (seconds: number) => string;
  getTimeColor: () => string;
  status: RoleplayStatus;
  hasFirstConversationCompleted: boolean;
  toggleRecording: () => void;
  handleEndSession: () => void;
  navigate: (path: string) => void;
  speakButtonRef: React.RefObject<HTMLButtonElement | null>;
}

export const RoleplayMobileControlBar: React.FC<RoleplayMobileControlBarProps> = ({
  sessionTime,
  estimatedTime,
  formatTime,
  getTimeColor,
  status,
  hasFirstConversationCompleted,
  toggleRecording,
  handleEndSession,
  navigate,
  speakButtonRef,
}) => {
  const theme = useTheme();
  return (
    <Box sx={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      bgcolor: '#333',
      borderTop: '1px solid #e0e0e0',
      padding: '16px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '16px',
      zIndex: 1300,
      boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
    }}>
      <Box sx={{ color: '#fff', display: 'flex', gap: '4px', alignItems: 'center' }}>
        <Box sx={{
          height: '20px',
          fontSize: '20px',
          fontFamily: 'HelveticaNeue,Meiryo,sans-serif',
          bgcolor: 'transparent',
          color: getTimeColor() === 'error' ? '#f44336' : (getTimeColor() === 'warning' ? '#ff9800' : '#fff'),
          borderRadius: '0px',
          fontWeight: 400,
          textAlign: 'right',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}>
          {formatTime(sessionTime)}
        </Box>
        /
        <Box sx={{
          height: '20px',
          fontSize: '20px',
          fontFamily: 'HelveticaNeue,Meiryo,sans-serif',
          bgcolor: 'transparent',
          color: '#fff',
          borderRadius: '0px',
          fontWeight: 400,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0px',
        }}>
          <span>{estimatedTime}</span>
          <span style={{ fontSize: '14px', marginLeft: '0px' }}>分</span>
        </Box>
      </Box>
      <Button
        variant="contained"
        color={status === 'recording' ? 'error' : 'success'}
        onClick={toggleRecording}
        disabled={status === 'aiSpeaking' || status === 'processing' || status === 'waiting'}
        ref={speakButtonRef as React.RefObject<HTMLButtonElement>}
        sx={{
          bgcolor: status === 'recording' ? theme.palette.error.main : '#5EB600',
          color: '#fff',
          borderRadius: '10px',
          padding: '4px',
          fontSize: '20px',
          fontWeight: 400,
          height: '46px',
          flex: 1,
          animation: status === 'recording' ? 'pulse 1.5s infinite' : 'none',
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)' },
            '70%': { boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' },
          },
        }}
      >
        <img src={micIcon} alt="マイクアイコン" style={{ width: '20px', height: '20px' }} />
      </Button>
      <Button
        variant="contained"
        onClick={handleEndSession}
        disabled={status === 'recording' || !hasFirstConversationCompleted}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          border: 'none',
          fontSize: '14px',
          fontWeight: 400,
          height: '46px',
          color: '#fff',
          bgcolor: '#333',
          flex: 1,
          whiteSpace: 'nowrap',
        }}
      >
        <img src={feedbackIcon} alt="フィードバックアイコン" style={{ width: '20px', height: '20px' }} />
      </Button>
      <Button
        variant="contained"
        onClick={() => navigate('/scenarios')}
        disabled={status === 'recording'}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          border: 'none',
          fontSize: '14px',
          fontWeight: 400,
          height: '46px',
          color: '#fff',
          bgcolor: '#333',
        }}
      >
        <img src={cancelIcon} alt="キャンセルアイコン" style={{ width: '20px', height: '20px' }} />
      </Button>
    </Box>
  );
};
